import React, { useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Check, ChevronDown, Plus, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";

/**
 * Multi-select with optional "Add New" trigger.
 * options: [{id, label}]
 * value: array of ids
 */
export default function MultiSelectWithAdd({
  options,
  value,
  onChange,
  onAddNew,
  placeholder = "Select…",
  testId = "multiselect",
}) {
  const [open, setOpen] = useState(false);
  const safeValue = Array.isArray(value) ? value : [];

  const toggle = (id) => {
    if (safeValue.includes(id)) onChange(safeValue.filter((v) => v !== id));
    else onChange([...safeValue, id]);
  };

  const selectedOptions = options.filter((o) => safeValue.includes(o.id));

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`${testId}-trigger`}
          className="w-full min-h-10 border vsm-border vsm-surface px-3 py-1.5 text-sm flex items-center gap-2 flex-wrap text-left rounded-none"
        >
          {selectedOptions.length === 0 && (
            <span className="vsm-muted">{placeholder}</span>
          )}
          {selectedOptions.map((o) => (
            <Badge
              key={o.id}
              data-testid={`${testId}-chip-${o.id}`}
              variant="secondary"
              className="rounded-none vsm-primary-bg gap-1"
            >
              {o.label}
              <span
                role="button"
                onClick={(e) => {
                  e.stopPropagation();
                  toggle(o.id);
                }}
                className="ml-1 cursor-pointer"
              >
                <X className="w-3 h-3" />
              </span>
            </Badge>
          ))}
          <ChevronDown className="w-4 h-4 ml-auto opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="rounded-none p-0 w-[var(--radix-popover-trigger-width)] border vsm-border"
        align="start"
        data-testid={`${testId}-popover`}
      >
        <div className="max-h-64 overflow-y-auto">
          {options.length === 0 && (
            <div className="px-3 py-3 text-sm vsm-muted">No options yet.</div>
          )}
          {options.map((o) => {
            const active = safeValue.includes(o.id);
            return (
              <button
                key={o.id}
                type="button"
                data-testid={`${testId}-option-${o.id}`}
                onClick={() => toggle(o.id)}
                className="w-full px-3 py-2 text-sm flex items-center gap-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 text-left"
              >
                <div
                  className={`w-4 h-4 border vsm-border flex items-center justify-center ${
                    active ? "vsm-primary-bg" : ""
                  }`}
                >
                  {active && <Check className="w-3 h-3" />}
                </div>
                <span>{o.label}</span>
              </button>
            );
          })}
        </div>
        {onAddNew && (
          <div className="border-t vsm-border">
            <Button
              type="button"
              variant="ghost"
              data-testid={`${testId}-add-new`}
              onClick={() => {
                setOpen(false);
                onAddNew();
              }}
              className="w-full justify-start rounded-none gap-2 font-medium"
            >
              <Plus className="w-4 h-4" /> Add new
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
